import { useStore } from "../../../store/store";
import { useTranslation } from "react-i18next";
import { fileDownloadUrl } from "../../types/liveFile";
import { useCallback, useState } from "react";
import { fetchPublicLiveFiles } from "../../api/liveFiles";
import Loader from "../../../components/Loader/Loader";

interface IProps {
	liveId: number | undefined;
}

const LivePublicDocuments = (props: IProps) => {
	const { t } = useTranslation();
	const publicLiveFiles = useStore((s) => s.publicLiveFiles);
	const setPublicLiveFiles = useStore((s) => s.setPublicLiveFiles);
	const [refetching, setRefetching] = useState(false);

	const refetch = useCallback(async () => {
		if (!props.liveId) return;
		try {
			setRefetching(true);
			const files = await fetchPublicLiveFiles(props.liveId);
			setPublicLiveFiles(files);
		} catch {
			console.error("Failed to refetch live documents");
		} finally {
			setRefetching(false);
		}
	}, [props.liveId, setPublicLiveFiles]);

	if (refetching && publicLiveFiles.length === 0) {
		return (
			<div className="flex h-full items-center justify-center">
				<Loader />
			</div>
		);
	}

	if (publicLiveFiles.length === 0) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="48"
					height="48"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.5"
					className="text-primary/30"
				>
					<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
					<polyline points="14 2 14 8 20 8" />
				</svg>
				<p className="text-sm text-primary/50">
					{t("live_page.no_live_documents")}
				</p>
				{props.liveId ? (
					<button
						type="button"
						className="text-sm text-primary underline"
						onClick={refetch}
					>
						{t("live_page.reload")}
					</button>
				) : null}
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col overflow-hidden">
			{refetching ? (
				<div className="flex justify-center p-2">
					<Loader />
				</div>
			) : null}
			<div className="min-h-0 flex-1 overflow-y-auto p-3">
				<ul className="flex flex-col gap-2">
					{publicLiveFiles.map((f) => (
						<li key={f.id}>
							<a
								href={fileDownloadUrl(f.file_path)}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={`${t("live_page.view_document")}: ${f.file_name || f.title}`}
								className="flex min-h-[2.75rem] items-center gap-2 rounded border border-zinc-200 bg-white px-2 py-2 text-left shadow-sm transition hover:border-zinc-300 hover:bg-zinc-50 sm:gap-3 sm:px-3"
							>
								<span
									className="shrink-0 text-emerald-600"
									aria-hidden
									title={t("live_page.view_document")}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="20"
										height="20"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
										<circle cx="12" cy="12" r="3" />
									</svg>
								</span>
								<span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-900">
									{f.file_name || f.title}
								</span>
								{(f.owner || "").trim() ? (
									<span className="max-w-[45%] shrink-0 truncate text-right text-xs text-zinc-600 sm:max-w-[40%] sm:text-sm">
										{(f.owner || "").trim()}
									</span>
								) : null}
							</a>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default LivePublicDocuments;
